import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, inject, TrackByFunction, AfterViewInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BadgeColor, TableAction, TableColumn } from '../../models/app.models';
import { ColumnFilter, getDefaultOperator } from './filter-types';

// Re-export for backward compatibility
export { TableAction, TableColumn } from '../../models/app.models';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() paginator: boolean = true;
  @Input() rows: number = 10;
  @Input() loading: boolean = false;
  @Input() showCheckbox: boolean = false;
  @Input() rowSelectionCallback?: (row: any) => boolean;
  selectedRows: Set<any> = new Set();

  @Output() onRowClick = new EventEmitter<any>();
  @Output() onActionClick = new EventEmitter<{action: string, row: any, checked?: boolean}>();
  @Output() onLinkClick = new EventEmitter<{field: string, row: any}>();
  @Output() onCheckboxChange = new EventEmitter<{row: any, checked: boolean}>();

  // Filter state
  filters: Map<string, ColumnFilter> = new Map();
  filteredData: any[] = [];
  openFilterColumn: string | null = null;
  filterTriggerElement: HTMLElement | null = null;
  openFilterColumnObj: TableColumn | null = null;
  
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private clickHandler: ((event: MouseEvent) => void) | null = null;
  
  sanitizeIcon(icon: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(icon);
  }

  ngOnInit(): void {
    this.filteredData = [...this.data];
  }

  ngAfterViewInit(): void {
    // Set up event delegation for custom template links after view init
    this.setupCustomTemplateClickHandlers();
  }

  ngOnDestroy(): void {
    // Clean up event listener
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
    }
  }

  private setupCustomTemplateClickHandlers(): void {
    // Remove existing handler if any
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
    }
    
    // Handle clicks on custom template links with data-action attribute
    this.clickHandler = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest('[data-action]') as HTMLElement;
      if (target) {
        const action = target.getAttribute('data-action');
        const bidId = target.getAttribute('data-bid-id');
        const row = this.filteredData.find((r: any) => r.biddingNumber === bidId);
        if (row && action) {
          event.preventDefault();
          event.stopPropagation();
          this.handleActionClick(action, row);
        }
      }
    };
    
    document.addEventListener('click', this.clickHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (changes['data'].firstChange) {
        this.filteredData = [...this.data];
      } else {
        this.applyFilters();
      }
      this.cdr.markForCheck();
    }
  }
  
  // TrackBy function for better *ngFor performance
  trackByIndex: TrackByFunction<any> = (index: number) => index;
  
  trackByField(field: string): TrackByFunction<any> {
    return (index: number, item: any) => {
      return item[field] || index;
    };
  }

  handleRowClick(row: any): void {
    this.onRowClick.emit(row);
  }

  handleActionClick(action: string, row: any, checked?: boolean): void {
    this.onActionClick.emit({ action, row, checked });
  }

  handleCheckboxActionClick(event: MouseEvent, action: TableAction, row: any): void {
    event.stopPropagation();

    if (!action.preventToggleWhen?.(row)) {
      return;
    }

    event.preventDefault();
    this.handleActionClick(action.action, row, !!this.getFieldValue(row, action.checkedField || action.action));
  }

  handleLinkClick(field: string, row: any): void {
    this.onLinkClick.emit({ field, row });
  }

  getFieldValue(row: any, field: string): any {
    return row[field];
  }

  getBadgeColor(row: any, col: TableColumn): BadgeColor {
    if (col.badgeColor) {
      if (typeof col.badgeColor === 'function') {
        return col.badgeColor(row);
      }
      return col.badgeColor;
    }
    
    // Default color mapping based on field value
    const value = this.getFieldValue(row, col.field);
    const valueStr = String(value).toLowerCase().trim();
    
    // Type field mapping
    if (col.field === 'type' || col.field === 'typeofTender' || col.field === 'tenderType') {
      if (valueStr === 'public') return 'blue';
      if (valueStr === 'private') return 'purple';
      if (valueStr === 'rfp') return 'orange';
      if (valueStr === 'rfq') return 'green';
      if (valueStr === 'organization') return 'blue';
      if (valueStr === 'individual') return 'green';
    }

    // Type field mapping
    if (col.field === 'isTC') {
      if (valueStr === 'true') return 'blue';
      if (valueStr === 'false') return 'purple';
    }
    
    // Status field mapping
    if (col.field === 'status') {
      if (valueStr === 'published') return 'green';
      if (valueStr === 'evaluation') return 'orange';
      if (valueStr === 'under evaluation') return 'orange';
      if (valueStr === 'draft') return 'yellow';
      if (valueStr === 'rejected' || valueStr === 'regret') return 'red';
      if (valueStr === 'awarded') return 'green';
      if (valueStr === 'approved') return 'green';
      if (valueStr === 'submitted') return 'blue';
      if (valueStr === 'confirmed' || valueStr === 'confirm') return 'green';
      if (valueStr === 'completed') return 'green';
      if (valueStr === 'pending') return 'orange';
      if (valueStr === 'cancelled') return 'red';
      if (valueStr === 'scheduled') return 'blue';
      if (valueStr === 'open') return 'blue';
      if (valueStr === 'closed') return 'green';
      if (valueStr === 'unsealing') return 'orange';
      if (valueStr === 'pendingevaluation') return 'orange';
      if (valueStr === 'underevaluation') return 'blue';
      if (valueStr === 'evaluated') return 'green';
    }

    // Workflow Status field mapping
    if (col.field === 'workflowStatus') {
      if (valueStr === 'published') return 'green';
      if (valueStr === 'pending publication' || valueStr === 'pendingpublication') return 'orange';
      if (valueStr === 'approved') return 'blue';
      if (valueStr === 'rejected') return 'red';
      if (valueStr === 'not submitted' || valueStr === 'notsubmitted') return 'purple';
    }
    if (col.field === 'localOrForeign') {
      if (valueStr === 'local') return 'green';
      if (valueStr === 'foreign') return 'purple';
    }
    
    // Voting Status field mapping
    if (col.field === 'votingStatus') {
      if (valueStr === 'unsealing') return 'orange';
      if (valueStr === 'sealed') return 'blue';
      if (valueStr === 'open') return 'green';
      if (valueStr === 'closed') return 'gray';
    }
    
    // Recommendation field mapping
    if (col.field === 'recommendation') {
      if (valueStr === 'award') return 'green';
      if (valueStr === 'pending') return 'gray';
      if (valueStr === 'defer') return 'orange';
    }
    
    // Evaluation Type field mapping
    if (col.field === 'evaluationType') {
      if (valueStr === 'both') return 'purple';
      if (valueStr === 'technical') return 'blue';
      if (valueStr === 'commercial') return 'green';
    }
    
    // Award Decision field mapping
    if (col.field === 'awardDecision') {
      if (valueStr === 'pending') return 'gray';
    }
    
    // Default to gray
    return 'gray';
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  isRowSelectedByCallback(row: any): boolean {
    if (this.rowSelectionCallback && typeof this.rowSelectionCallback === 'function') {
      return this.rowSelectionCallback(row);
    }
    return false;
  }

  toggleRowSelection(row: any, event: any): void {
    const checked = event.target.checked;
    if (checked) {
      this.selectedRows.add(row);
    } else {
      this.selectedRows.delete(row);
    }
    this.onCheckboxChange.emit({ row, checked });
  }

  toggleAllRows(event: any): void {
    const checked = event.target.checked;
    if (checked) {
      this.filteredData.forEach(row => this.selectedRows.add(row));
    } else {
      this.selectedRows.clear();
    }
    this.filteredData.forEach(row => {
      this.onCheckboxChange.emit({ row, checked });
    });
  }

  // Filter methods
  isFilterable(column: TableColumn): boolean {
    return column.filterable !== false && column.type !== 'action';
  }

  hasFilter(column: TableColumn): boolean {
    return this.filters.has(column.field);
  }

  toggleFilter(column: TableColumn, event: Event): void {
    event.stopPropagation();
    if (this.openFilterColumn === column.field) {
      this.openFilterColumn = null;
      this.openFilterColumnObj = null;
      this.filterTriggerElement = null;
    } else {
      this.openFilterColumn = column.field;
      this.openFilterColumnObj = column;
      // Get the button element that triggered the filter
      const target = event.target as HTMLElement;
      const button = target.closest('button.filter-icon-btn') as HTMLElement;
      this.filterTriggerElement = button;
    }
  }

  onFilterApply(column: TableColumn, filter: ColumnFilter | null): void {
    if (filter) {
      this.filters.set(column.field, filter);
    } else {
      this.filters.delete(column.field);
    }
    this.openFilterColumn = null;
    this.openFilterColumnObj = null;
    this.filterTriggerElement = null;
    this.applyFilters();
  }

  onFilterClear(column: TableColumn): void {
    this.filters.delete(column.field);
    this.openFilterColumn = null;
    this.openFilterColumnObj = null;
    this.filterTriggerElement = null;
    this.applyFilters();
  }

  onSortApply(column: TableColumn, direction: 'asc' | 'desc' | null): void {
    const existingFilter = this.filters.get(column.field);
    if (existingFilter) {
      existingFilter.sortDirection = direction;
      this.filters.set(column.field, existingFilter);
    } else if (direction) {
      const filter: ColumnFilter = {
        field: column.field,
        operator: getDefaultOperator(column.type || 'text'),
        values: [],
        sortDirection: direction
      };
      this.filters.set(column.field, filter);
    }
    this.applyFilters();
  }

  onFilterDropdownClosed(column: TableColumn): void {
    if (this.openFilterColumn === column.field) {
      this.openFilterColumn = null;
      this.openFilterColumnObj = null;
      this.filterTriggerElement = null;
    }
  }

  getUniqueValues(column: TableColumn): any[] {
    const values = new Set<any>();
    this.data.forEach(row => {
      const value = this.getFieldValue(row, column.field);
      if (value != null && value !== '') {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  }

  getCurrentFilter(column: TableColumn): ColumnFilter | null {
    return this.filters.get(column.field) || null;
  }

  applyFilters(): void {
    let result = [...this.data];

    // Apply all filters
    this.filters.forEach((filter, field) => {
      if (filter.values.length > 0 || filter.operator === 'today' || filter.operator === 'thisWeek' || filter.operator === 'thisMonth') {
        result = result.filter(row => this.matchesFilter(row, filter));
      }
    });

    // Apply sorting
    const sortFilters = Array.from(this.filters.values()).filter(f => f.sortDirection);
    if (sortFilters.length > 0) {
      // Use the first sort filter (can be enhanced to support multiple sorts)
      const sortFilter = sortFilters[0];
      result = this.sortData(result, sortFilter.field, sortFilter.sortDirection!);
    }

    this.filteredData = result;
    this.cdr.markForCheck();
  }

  private matchesFilter(row: any, filter: ColumnFilter): boolean {
    const value = this.getFieldValue(row, filter.field);
    const operator = filter.operator;

    // Handle null/undefined values
    if (value == null || value === '') {
      // For "does not equal" and "does not contain", null/empty values should match
      if (operator === 'doesNotEqual' || operator === 'doesNotContain' || operator === 'isNotOneOf') {
        return true;
      }
      return false;
    }

    switch (operator) {
      case 'equals':
        return String(value).toLowerCase() === String(filter.values[0]).toLowerCase();
      case 'doesNotEqual':
        return String(value).toLowerCase() !== String(filter.values[0]).toLowerCase();
      case 'contains':
        return String(value).toLowerCase().includes(String(filter.values[0]).toLowerCase());
      case 'doesNotContain':
        return !String(value).toLowerCase().includes(String(filter.values[0]).toLowerCase());
      case 'startsWith':
        return String(value).toLowerCase().startsWith(String(filter.values[0]).toLowerCase());
      case 'endsWith':
        return String(value).toLowerCase().endsWith(String(filter.values[0]).toLowerCase());
      case 'greaterThan':
        return Number(value) > Number(filter.values[0]);
      case 'lessThan':
        return Number(value) < Number(filter.values[0]);
      case 'between':
        // Check if this is a date or number column based on filter values
        if (filter.values[0] instanceof Date || filter.values[1] instanceof Date || 
            (typeof filter.values[0] === 'string' && filter.values[0].includes('-'))) {
          // Date range
          const dateFrom = filter.values[0] ? new Date(filter.values[0]) : null;
          const dateTo = filter.values[1] ? new Date(filter.values[1]) : null;
          const rowDate = new Date(value);
          if (dateFrom && dateTo) {
            return rowDate >= dateFrom && rowDate <= dateTo;
          } else if (dateFrom) {
            return rowDate >= dateFrom;
          } else if (dateTo) {
            return rowDate <= dateTo;
          }
          return true;
        } else {
          // Number range
          const min = filter.values[0] != null ? Number(filter.values[0]) : -Infinity;
          const max = filter.values[1] != null ? Number(filter.values[1]) : Infinity;
          return Number(value) >= min && Number(value) <= max;
        }
      case 'before':
        return new Date(value) < new Date(filter.values[0]);
      case 'after':
        return new Date(value) > new Date(filter.values[0]);
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const rowDate = new Date(value);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate.getTime() === today.getTime();
      case 'thisWeek':
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const rowDateWeek = new Date(value);
        return rowDateWeek >= weekStart && rowDateWeek <= weekEnd;
      case 'thisMonth':
        const now = new Date();
        const rowDateMonth = new Date(value);
        return rowDateMonth.getMonth() === now.getMonth() && rowDateMonth.getFullYear() === now.getFullYear();
      case 'isOneOf':
        return filter.values.includes(value);
      case 'isNotOneOf':
        return !filter.values.includes(value);
      default:
        return true;
    }
  }

  private sortData(data: any[], field: string, direction: 'asc' | 'desc'): any[] {
    return [...data].sort((a, b) => {
      const aValue = this.getFieldValue(a, field);
      const bValue = this.getFieldValue(b, field);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return direction === 'asc' ? -1 : 1;
      if (bValue == null) return direction === 'asc' ? 1 : -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }

  clearAllFilters(): void {
    this.filters.clear();
    this.openFilterColumn = null;
    this.openFilterColumnObj = null;
    this.filterTriggerElement = null;
    this.applyFilters();
  }

  hasAnyFilters(): boolean {
    return this.filters.size > 0;
  }
}

